<script setup lang="ts">
import type { ContentFile } from '~/models/Content/ContentFile'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { faDownload } from '@fortawesome/duotone-regular-svg-icons'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import Button from '~/components/ui/Button.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = defineProps<{
  resource: BaseItem | ContentFile
}>()

const { t } = useI18n()
const { buildResourceUrl } = useUrlBuilder()
const { isFile, getTitle, isPdf } = useContentHelper()
const { readableMimeType, readableFileSize } = useFileInfo()

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const downloadable = computed(() => isFile(props.resource))
const transitionEffect = computed(() => !isFile(props.resource) && !isPdf(props.resource))

const mimeType = computed(() => (props.resource as ContentFile)?.file?.mimeType
  ? readableMimeType((props.resource as ContentFile)?.file?.mimeType || '')
  : '')

const fileSize = computed(() => (props.resource as ContentFile).file?.fileSize
  ? readableFileSize((props.resource as ContentFile)?.file?.fileSize || 0)
  : '')

const fileInfo = computed(() => [mimeType.value, fileSize.value].filter(Boolean).join(' '))
</script>

<template>
  <div class="flex items-center justify-between px-4 py-3">
    <div class="space-y-1">
      <span class="text-base font-semibold hyphens-auto text-blue-50 sm:text-lg">
        <LinkOrAnchor
          class="hover:underline focus-visible:underline"
          :link="link"
          :transition-effect="transitionEffect"
        >
          {{ title }}
        </LinkOrAnchor>
      </span>
      <p
        v-if="fileInfo"
        class="text-gray-40 text-sm"
        v-text="fileInfo"
      />
    </div>
    <div class="flex gap-2">
      <Button
        v-if="downloadable"
        class="invisible md:visible"
        size="sm"
        shape="square"
        :title="t('download')"
        :href="link"
      >
        <font-awesome-icon :icon="faDownload" />
      </Button>
      <CardMenu :resource="resource" button-classes="z-10" />
    </div>
  </div>
</template>
